import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import CTA from '../cta';
import LoadingBar from '../loading-bar';
import RichText from '../rich-text/base';
import {
  CTAWrapper,
  Heading,
  SubText,
  HiddenMobile,
  LinkCTA,
  TimeAmount,
  Timer,
  TimeUnit,
  Wrapper,
} from './styles';

const expiryStatuses = {
  EXPIRED: 'expired',
  NOT_EXPIRED: 'notExpired',
};

const getTimerBackgroundColor = (isTimerHighlighted, backgroundColor) => {
  if (!isTimerHighlighted) {
    return backgroundColor;
  }

  if (backgroundColor === 'White') {
    return 'Black';
  }

  if (backgroundColor === 'Black') {
    return 'White';
  }
};

const formatTimeAmount = (amount) => {
  if (amount < 10) {
    return `0${amount}`;
  }

  return amount;
};

const calculateTimeUnits = (isExpired, timeRemaining) => ({
  days: {
    label: <div>Days</div>,
    amount: formatTimeAmount(
      isExpired ? 0 : Math.floor(timeRemaining / 3600000 / 24)
    ),
  },
  hours: {
    label: (
      <div>
        H<HiddenMobile>ou</HiddenMobile>rs
      </div>
    ),
    amount: formatTimeAmount(
      isExpired ? 0 : Math.floor((timeRemaining / 3600000) % 24)
    ),
  },
  minutes: {
    label: (
      <div>
        Min<HiddenMobile>ute</HiddenMobile>s
      </div>
    ),
    amount: formatTimeAmount(
      isExpired ? 0 : Math.floor((timeRemaining / 60000) % 60)
    ),
  },
  seconds: {
    label: (
      <div>
        Sec<HiddenMobile>ond</HiddenMobile>s
      </div>
    ),
    amount: formatTimeAmount(
      isExpired ? 0 : Math.floor((timeRemaining / 1000) % 60)
    ),
  },
});

const CountdownTimer = ({
  date,
  titlePreExpiry,
  titlePostExpiry,
  subtextPreExpiry,
  subtextPostExpiry,
  ctaPreExpiry,
  ctaLinkPostExpiry,
  ctaTextPostExpiry,
  backgroundColor,
}) => {
  const [timeRemaining, setTimeRemaining] = useState();

  useEffect(() => {
    setTimeRemaining(date.getTime() - new Date().getTime());
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining(date.getTime() - new Date().getTime());
    }, 1000);

    return () => clearInterval(interval);
  }, [date]);

  const isExpired = timeRemaining <= 0;

  const expiryStatus = isExpired
    ? expiryStatuses.EXPIRED
    : expiryStatuses.NOT_EXPIRED;

  const { days, hours, minutes, seconds } = calculateTimeUnits(
    isExpired,
    timeRemaining
  );
  const isTimerHighlighted = days.amount < 5 && !isExpired;

  const contentToDisplay = {
    expired: {
      title: titlePostExpiry,
      subtext: subtextPostExpiry,
      cta: {
        buttonText: ctaTextPostExpiry,
        link: ctaLinkPostExpiry,
      },
    },
    notExpired: {
      title: titlePreExpiry,
      subtext: subtextPreExpiry,
      cta: {
        buttonText: ctaPreExpiry.buttonText,
        link: ctaPreExpiry.link,
      },
    },
  };

  return (
    <Wrapper $backgroundColor={backgroundColor} $hasDataLoaded={timeRemaining}>
      {timeRemaining ? (
        <>
          <Heading id="countdown-timer-heading">
            {contentToDisplay[expiryStatus].title}
          </Heading>
          <SubText richText={contentToDisplay[expiryStatus].subtext} />
          <Timer
            $isTimerHighlighted={isTimerHighlighted}
            $backgroundColor={getTimerBackgroundColor(
              isTimerHighlighted,
              backgroundColor
            )}
            role="timer"
            aria-labelledby="countdown-timer-heading"
          >
            <div>
              <TimeAmount>{days.amount}</TimeAmount>
              <TimeUnit>{days.label}</TimeUnit>
            </div>
            <div>:</div>
            <div>
              <TimeAmount>{hours.amount}</TimeAmount>
              <TimeUnit>{hours.label}</TimeUnit>
            </div>
            <div>:</div>
            <div>
              <TimeAmount>{minutes.amount}</TimeAmount>
              <TimeUnit>{minutes.label}</TimeUnit>
            </div>
            <div>:</div>
            <div>
              <TimeAmount>{seconds.amount}</TimeAmount>
              <TimeUnit>{seconds.label}</TimeUnit>
            </div>
          </Timer>
          <CTAWrapper>
            {!isExpired && contentToDisplay[expiryStatus].cta ? (
              <CTA type="standard" bg="primary" link={ctaPreExpiry.link}>
                {ctaPreExpiry.buttonText}
              </CTA>
            ) : null}
          </CTAWrapper>
          {isExpired && contentToDisplay[expiryStatus].cta ? (
            <LinkCTA
              link={ctaLinkPostExpiry}
              $backgroundColor={backgroundColor}
            >
              {ctaTextPostExpiry}
            </LinkCTA>
          ) : null}
        </>
      ) : (
        <LoadingBar />
      )}
    </Wrapper>
  );
};

CountdownTimer.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired,
  titlePreExpiry: PropTypes.string.isRequired,
  titlePostExpiry: PropTypes.string.isRequired,
  subtextPreExpiry: PropTypes.string,
  subtextPostExpiry: PropTypes.string,
  ctaPreExpiry: PropTypes.shape({
    buttonText: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
  }),
  ctaLinkPostExpiry: PropTypes.string,
  ctaTextPostExpiry: PropTypes.string,
  backgroundColor: PropTypes.oneOf(['White', 'Black']),
};

export default CountdownTimer;

export const CountdownTimerAssembly = ({ contentfulData }) => (
  <CountdownTimer
    date={new Date(contentfulData.date)}
    titlePreExpiry={contentfulData.titlePreExpiry}
    titlePostExpiry={contentfulData.titlePostExpiry}
    subtextPreExpiry={contentfulData.subtextPreExpiry}
    subtextPostExpiry={contentfulData.subtextPostExpiry}
    ctaPreExpiry={contentfulData.ctaPreExpiry}
    ctaLinkPostExpiry={contentfulData.ctaLinkPostExpiry}
    ctaTextPostExpiry={contentfulData.ctaTextPostExpiry}
    backgroundColor={contentfulData.backgroundColor}
  />
);

CountdownTimerAssembly.propTypes = {
  contentfulData: PropTypes.object,
};
